import { useEffect, useRef, useState } from 'react';

// Default polling interval: 60 seconds
const DEFAULT_POLLING_INTERVAL_MS = 60000;

interface UseInboxPollingOptions {
  enabled: boolean;
  intervalMs?: number;
  onRefresh: () => void;
}

/**
 * Hook to enable periodic polling for Operations Inbox data
 *
 * Features:
 * - Configurable polling interval (default: 60 seconds)
 * - Can be enabled/disabled
 * - Automatic cleanup on unmount
 * - Tab visibility detection (pauses when tab hidden)
 *
 * @example
 * ```typescript
 * useInboxPolling({
 *   enabled: true,
 *   intervalMs: 60000,
 *   onRefresh: refetch,
 * });
 * ```
 */
export function useInboxPolling({
  enabled,
  intervalMs = DEFAULT_POLLING_INTERVAL_MS,
  onRefresh,
}: UseInboxPollingOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onRefreshRef = useRef(onRefresh);
  const [isTabActive, setIsTabActive] = useState(!document.hidden);

  // Keep onRefresh ref up to date without recreating the interval
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  // Track tab visibility to pause polling when tab hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabActive(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Set up polling interval
  useEffect(() => {
    // Only poll when enabled AND tab is active
    const shouldPoll = enabled && isTabActive;

    if (!shouldPoll) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Set up polling interval
    intervalRef.current = setInterval(() => {
      onRefreshRef.current();
    }, intervalMs);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, isTabActive, intervalMs]);
}
